import { format } from 'date-fns';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { Branch } from '../../../@types/Branch';
import useBranchHierachy from '../../../data/branch/useBranchHierarchy';
import type { VehicleListItem } from '../../../data/vehicles/useVehicles';
import Badge from '../../../styled-components/components/Badge';
import BulletSeparatedList from '../../../styled-components/components/BulletSeparatedList';
import { Column, Columns } from '../../../styled-components/components/Columns';
import Highlight from '../../../styled-components/components/Highlight';
import { IconCar, IconExclamation } from '../../../styled-components/components/Icon';
import { Link } from '../../../styled-components/components/Link';
import Loader from '../../../styled-components/components/Loader';
import MediaPreview from '../../../styled-components/components/MediaPreview';
import Notification from '../../../styled-components/components/Notification';
import { Skeleton } from '../../../styled-components/components/Skeleton';
import { StackSmall, StackTiny } from '../../../styled-components/components/Stack';
import { CellRouterLink } from '../../../styled-components/components/Table';
import { Text } from '../../../styled-components/components/Text';
import Tooltip from '../../../styled-components/components/Tooltip';
import { size } from '../../../styled-components/layout/helpers';
import InlineMediaContent from '../../../styled-components/structures/InlineMediaContent';
import { urlToCfit } from '../../../utils/cfit';
import { currency } from '../../../utils/currency';
import { PrettyNumber } from '../../../utils/string-formatter';
import { purchaseInfoTypesLabel } from '../Item/Shared/StatusInformationAside/PurchaseInfoType/PurchaseInfoHelpers';
import { resolveBranch } from './utils';

const WarningIcon = styled.div`
  color: white;
  margin: -5px;
`;

// Add spacing and alignment between title and tags
const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${size(0.5)};
`;

const BranchWrapper = styled(Text)`
  ${(props) => props.theme.breakpoint.LtSm} {
    display: none;
  }
`;

export const SkeletonLoader = () => {
  return (
    <>
      <Columns $spacing={1}>
        <Column>
          <Skeleton $width="64px" $height="64px" />
        </Column>
        <Column $noShrink $grow>
          <StackSmall>
            <Skeleton $width="40%" $height="1.125rem" />
          </StackSmall>
          <StackSmall>
            <Skeleton $width="50%" $height="0.875rem" />
          </StackSmall>
          <StackSmall>
            <Skeleton $width="30%" $height="0.75rem" />
          </StackSmall>
        </Column>
      </Columns>
    </>
  );
};

interface VehicleDescriptionProps {
  isVisible: boolean;
  vehicle: VehicleListItem;
  isParent: boolean;
  showOrganization?: boolean;
  branchId: string | undefined;
  branches: Branch[];
  enableDetailsToggle?: boolean;
  wholeRowClickable?: boolean;
  highlight?: string;
  children?: React.ReactNode;
}
const VehicleDescription = ({
  isVisible,
  vehicle,
  isParent,
  showOrganization,
  branchId,
  branches,
  wholeRowClickable,
  highlight,
  children,
}: VehicleDescriptionProps) => {
  const { t, i18n } = useTranslation([
    'salesAndPurchasingVirtualVehicles',
    'salesAndPurchasingVehicles',
    'validation',
  ]);
  const [branchHierachy, requestBranchHierachy] = useBranchHierachy();
  const branchName = useMemo(
    () => resolveBranch(vehicle.branchId, branchHierachy),
    [branchHierachy, vehicle.branchId],
  );

  const equipmentLevel = vehicle.vehicle.equipmentLevelsName
    ? ` – ${vehicle.vehicle.equipmentLevelsName}`
    : '';

  const vehicleDataList = [
    <Highlight
      key={vehicle.vehicle.vin}
      text={`${vehicle.vehicle.manufacturer} ${vehicle.vehicle.salesName}${equipmentLevel}`}
      highlight={highlight}
    />,
    vehicle.vehicle.modelYear,
    `${PrettyNumber(vehicle.vehicle.odometerReading.value)} ${
      vehicle.vehicle.odometerReading.unit
    }`,
  ];

  if (vehicle.ad.price) {
    vehicleDataList.push(currency(PrettyNumber(vehicle.ad.price)));
  }

  const image = vehicle.ad.images?.[0]?.url;

  const virtual = vehicle.isVirtual && !vehicle.vehicle.vin && !vehicle.vehicle.registrationNumber;
  const virtualWithVIN =
    vehicle.isVirtual && !!vehicle.vehicle.vin && !vehicle.vehicle.registrationNumber;

  const createDmsTooltip = useMemo(() => {
    const list: string[] = [];
    if (!vehicle.dms) return undefined;
    if (vehicle.dms.importedAt) {
      list.push(
        i18n.t('tooltip.imported', {
          ns: 'salesAndPurchasingVehicles',
          date: format(new Date(vehicle.dms.importedAt), 'yyyy-MM-dd HH:mm'),
        }),
      );
    }
    if (vehicle.dms.updatedAt) {
      list.push(
        i18n.t('tooltip.updated', {
          ns: 'salesAndPurchasingVehicles',
          date: format(new Date(vehicle.dms.updatedAt), 'yyyy-MM-dd HH:mm'),
        }),
      );
    }
    if (vehicle.dms.deletedAt) {
      list.push(
        i18n.t('tooltip.deleted', {
          ns: 'salesAndPurchasingVehicles',
          date: format(new Date(vehicle.dms.deletedAt), 'yyyy-MM-dd HH:mm'),
        }),
      );
    }
    return list.join('\r\n');
  }, [vehicle.dms]);

  const hasAccessToVehicle = branches.map((branch) => branch.id).includes(vehicle.branchId);
  const isOnSameBranch = branchId === vehicle.branchId;

  const vehicleTitle =
    vehicle.vehicle.registrationNumber || vehicle.vehicle.vin || t('unknown', { ns: 'validation' });

  return (
    <InlineMediaContent
      media={
        <MediaPreview
          image={
            image && isVisible
              ? {
                  src: urlToCfit(image, { width: 225 })!,
                  alt: vehicle.ad.title || t('logotype', { ns: 'glossary' }),
                }
              : undefined
          }
          icon={!image || !isVisible ? <IconCar /> : undefined}
          notification={
            vehicle.enrichment === 'Pending' && (
              <Notification tone="alt">
                <WarningIcon>
                  <IconExclamation size="1.5em" />
                </WarningIcon>
              </Notification>
            )
          }
        />
      }
    >
      {vehicle.archivedAt && (
        <Text $tone="error" $size="xsmall" $weight="medium">
          {t('archived.archived', { ns: 'salesAndPurchasingVehicles' })}
        </Text>
      )}
      <TitleWrapper>
        {isParent || !isOnSameBranch ? (
          <>
            <Text $weight="bold">
              <Highlight text={vehicleTitle} highlight={highlight} />
            </Text>
          </>
        ) : (
          <CellRouterLink
            to={`/${branchId}/vehicles/${
              vehicle.isVirtual ? 'administrate-virtual-vehicles' : 'administrate-vehicles'
            }/${vehicle.id}`}
            title={
              t('showCategory', {
                ns: 'common',
                category: `${vehicle.vehicle.registrationNumber} (${vehicle.ad.title} ${vehicle.ad.shortDescription})`,
              }) || ''
            }
            $hoverGhost={wholeRowClickable}
          >
            <Highlight text={vehicleTitle} highlight={highlight} />
          </CellRouterLink>
        )}
        {!hasAccessToVehicle && (
          <Badge tone="disabled" size="small">
            {t('noAdminAccess', { ns: 'salesAndPurchasingVehicles' })}
          </Badge>
        )}
        {virtual ||
          (virtualWithVIN && (
            <Badge tone="contrast" size="small">
              {t(virtualWithVIN ? 'virtual+vin' : 'virtual', {
                ns: 'salesAndPurchasingVirtualVehicles',
              })}
            </Badge>
          ))}
        {vehicle.dms ? (
          <Tooltip tooltip={createDmsTooltip} $whiteSpace="pre">
            <Badge tone="contrast" size="small">
              {vehicle.dms.deletedAt
                ? vehicle.dms.name
                  ? t('deletedFrom', {
                      ns: 'salesAndPurchasingVehicles',
                      name: vehicle.dms.name,
                    })
                  : t('deletedFromUnknown', {
                      ns: 'salesAndPurchasingVehicles',
                    })
                : vehicle.dms.name}
            </Badge>
          </Tooltip>
        ) : (
          vehicle.enrichment === 'Imported' && (
            <Badge tone="contrast" size="small">
              {t('importedFromUnknown', { ns: 'salesAndPurchasingVehicles' })}
            </Badge>
          )
        )}
        {vehicle.purchaseInfo?.type &&
          !['urn:wayke:vehicle:purchase-type:unknown'].includes(vehicle.purchaseInfo.type) && (
            <Badge tone="contrast" size="small">
              {purchaseInfoTypesLabel(vehicle.purchaseInfo.type)}
            </Badge>
          )}
      </TitleWrapper>
      <StackTiny>
        <BulletSeparatedList items={vehicleDataList} tone="alt" size="small" />
      </StackTiny>
      {showOrganization && (
        <StackTiny>
          <BranchWrapper $tone="alt" $size="xsmall">
            {requestBranchHierachy.isLoading ? (
              <Loader inlineBlock />
            ) : (
              <Columns $spacing={1}>
                <Column>{branchName}</Column>
                {!isOnSameBranch && hasAccessToVehicle && (
                  <Column>
                    <Link
                      href={`/${vehicle.branchId}/vehicles/${vehicle.isVirtual ? 'administrate-virtual-vehicles' : 'administrate-vehicles'}/${vehicle.id}`}
                      rel="noopener noreferer"
                      target="_blank"
                    >
                      {t('switchBranchAndGoToVehicle', { ns: 'salesAndPurchasingVehicles' })}
                    </Link>
                  </Column>
                )}
              </Columns>
            )}
          </BranchWrapper>
        </StackTiny>
      )}
      {children}
    </InlineMediaContent>
  );
};

export default VehicleDescription;
